import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { IoInformationCircleOutline } from 'react-icons/io5';
import { signInWithEmailPassword, signInWithSocial, signUpWithEmailPassword } from '../../services/authService';
import { isInRnWebView } from '../../bridge/webviewBridge';
import logoImg from '../../assets/logo-work-transparent.png';
import { db } from '../../firebase/config';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

const KakaoIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48">
    <path fill="#3C1E1E" d="M24 12c-7.73 0-14 4.95-14 11.06 0 3.94 2.62 7.39 6.56 9.34l-1.68 6.14c-.13.47.41.84.82.57l7.34-4.84c.32.02.64.04.96.04 7.73 0 14-4.95 14-11.06S31.73 12 24 12z"/>
  </svg>
);

const AppleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path fill="#000000" d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </svg>
);

const isIosWebView = () => {
  return isInRnWebView() && /iPhone|iPad|iPod/i.test(navigator.userAgent);
};

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    if (location.state?.toast) {
      setToast(location.state.toast);
      window.history.replaceState({}, '');
      const timer = setTimeout(() => setToast(''), 4000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 4000);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('아이디와 비밀번호를 입력해주세요.');
      return;
    }
    setBusy(true);
    setError('');

    try {
      const q = query(collection(db, 'users'), where('loginId', '==', email.trim()));
      const snap = await getDocs(q);

      if (!snap.empty) {
        const userDoc = snap.docs[0];
        const userData = userDoc.data();

        if (userData.password !== password) {
          setError('아이디 또는 비밀번호가 올바르지 않습니다.');
          return;
        }

        if (userData.status && userData.status !== 'approved') {
          showToast('승인 대기중입니다.\n관리자 승인 후 이용 가능합니다.');
          return;
        }

        const fakeEmail = `${email.trim()}@jogunbiz.local`;

        let authUid;
        try {
          const result = await signInWithEmailPassword({ email: fakeEmail, password, skipEnsure: true });
          authUid = result.uid;
        } catch (authErr) {
          const newUser = await signUpWithEmailPassword({ email: fakeEmail, password });
          authUid = newUser.uid;
        }
        if (authUid && userData.authUid !== authUid) {
          await updateDoc(doc(db, 'users', userDoc.id), { authUid });
        }

        navigate('/', { replace: true });
      } else {
        showToast('등록되지 않은 아이디입니다.\n회원가입을 진행해주세요.');
      }
    } catch (err) {
      console.error('로그인 실패:', err);
      setError('로그인에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setBusy(false);
    }
  };

  const handleSocial = async (provider) => {
    setBusy(true);
    setError('');
    try {
      await signInWithSocial({ provider });
      navigate('/', { replace: true });
    } catch (err) {
      console.error('소셜 로그인 실패:', err);
      setError(err.message || '소셜 로그인에 실패했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const handleSignUp = () => {
    navigate('/phone-verify', { state: { fromSocial: false } });
  };

  return (
    <Container>
      {busy && <Overlay><Spinner /></Overlay>}
      {toast && <Toast><IoInformationCircleOutline size={20} />{toast}</Toast>}

      <TopArea>
        <LogoImage src={logoImg} alt="WORK OUR PARTNERS" />
      </TopArea>

      <FormArea>
        <form onSubmit={handleLogin}>
          <InputGroup>
            <InputLabel>아이디</InputLabel>
            <StyledInput
              type="text"
              placeholder="아이디를 입력하세요"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </InputGroup>

          <InputGroup>
            <InputLabel>비밀번호</InputLabel>
            <StyledInput
              type="password"
              placeholder="비밀번호를 입력하세요"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </InputGroup>

          {error && <ErrorText>{error}</ErrorText>}

          <LoginButton type="submit" disabled={busy}>
            {busy ? '로그인 중...' : '로그인'}
          </LoginButton>
        </form>

        <FindLinks>
          <FindLink onClick={() => navigate('/find-id')}>아이디 찾기</FindLink>
          <FindDot />
          <FindLink onClick={() => navigate('/find-password')}>비밀번호 찾기</FindLink>
        </FindLinks>

        <Divider><DividerLine /><DividerText>간편 로그인</DividerText><DividerLine /></Divider>

        <SocialRow>
          {isIosWebView() ? (
            <SocialButton $apple onClick={() => handleSocial('apple')} disabled={busy}>
              <AppleIcon />
              <SocialLabel>Apple</SocialLabel>
            </SocialButton>
          ) : (
            <>
              <SocialButton onClick={() => handleSocial('google')} disabled={busy}>
                <GoogleIcon />
                <SocialLabel>Google</SocialLabel>
              </SocialButton>
              <SocialButton $kakao onClick={() => handleSocial('kakao')} disabled={busy}>
                <KakaoIcon />
                <SocialLabel>Kakao</SocialLabel>
              </SocialButton>
            </>
          )}
        </SocialRow>

        <SignUpArea>
          계정이 없으신가요? <SignUpLink onClick={handleSignUp}>회원가입</SignUpLink>
        </SignUpArea>

      </FormArea>
    </Container>
  );
};

export default LoginPage;

/* ── Styled Components ── */

const Container = styled.div`
  min-height: 100vh;
  background: #FFFFFF;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 24px 40px;
`;

const TopArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 0 20px;
`;

const LogoImage = styled.img`
  width: 240px;
  height: auto;
  object-fit: contain;
`;

const FormArea = styled.div`
  width: 100%;
`;

const InputGroup = styled.div`
  margin-bottom: 16px;
`;

const InputLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #3D4F5F;
  margin-bottom: 8px;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 1.5px solid #D9DDD6;
  border-radius: 10px;
  font-size: 15px;
  color: #1E2A32;
  background: #FFFFFF;
  transition: all 0.2s;

  &::placeholder {
    color: #8C9AA4;
  }

  &:focus {
    border-color: #4A6741;
    box-shadow: 0 0 0 3px rgba(74, 103, 65, 0.08);
  }
`;

const ErrorText = styled.p`
  color: #C0392B;
  font-size: 13px;
  margin-top: -4px;
  margin-bottom: 8px;
`;

const LoginButton = styled.button`
  width: 100%;
  padding: 15px;
  border-radius: 10px;
  background: linear-gradient(135deg, #3B5534 0%, #4A6741 100%);
  color: #FFFFFF;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 1px;
  margin-top: 8px;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(74, 103, 65, 0.25);
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const FindLinks = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 18px;
`;

const FindLink = styled.span`
  font-size: 13px;
  color: #5A6B75;
  cursor: pointer;
  transition: color 0.2s;
  &:hover { color: #4A6741; }
`;

const FindDot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #D9DDD6;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 28px 0 20px;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: #D9DDD6;
`;

const DividerText = styled.span`
  font-size: 12px;
  color: #8C9AA4;
  white-space: nowrap;
  letter-spacing: 1px;
`;

const SocialRow = styled.div`
  display: flex;
  gap: 12px;
`;

const SocialButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 13px;
  border-radius: 10px;
  border: 1.5px solid ${({ $kakao, $apple }) => $kakao ? '#FEE500' : $apple ? '#000000' : '#D9DDD6'};
  background: ${({ $kakao, $apple }) => $kakao ? '#FEE500' : $apple ? '#000000' : '#FFFFFF'};
  ${({ $apple }) => $apple && 'color: #FFFFFF;'}
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  &:disabled { opacity: 0.5; }
`;

const SocialLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: inherit;
`;

const SignUpArea = styled.p`
  text-align: center;
  margin-top: 24px;
  font-size: 13px;
  color: #5A6B75;
`;

const SignUpLink = styled.span`
  color: #4A6741;
  font-weight: 600;
  cursor: pointer;
  &:hover { text-decoration: underline; }
`;


const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(44, 54, 64, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
`;

const Spinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top-color: #C5A55A;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const Toast = styled.div`
  position: fixed;
  top: 60px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 10px;
  background: #FFFFFF;
  color: #1E2A32;
  padding: 14px 20px;
  border-radius: 12px;
  font-size: 14px;
  z-index: 999;
  line-height: 1.6;
  white-space: pre-line;
  min-width: 270px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  border: 1px solid #E8E8E8;
  animation: fadeIn 0.3s;
  @keyframes fadeIn { from { opacity: 0; transform: translateX(-50%) translateY(-10px); } }
`;
